import { Dices } from "lucide-react";
import { Switch } from "./ui/switch";
import { Field, inputCls } from "./Chips";
import { LyricsEditor } from "./LyricsEditor";
import { ModelSelect } from "./ModelSelect";
import { setPref } from "../lib/api";
import { buildStyle, engineCost, engineMeta, ENGINES, findItem, fmtUsd, IDEAS, LLMS } from "../lib/presets";
import { compareOptions } from "../hooks/useCreateForm";

const Toggle = ({ label, checked, onChange, testId, disabled }) => (
  <label className={`flex items-center gap-2.5 text-sm text-zinc-300 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
    <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} disabled={disabled} />{label}
  </label>
);

const engineHint = (e) => {
  if (e.instrumentalOnly) return "Šis variklis kuria tik instrumentinę muziką – žodžiai ir vokalas nenaudojami.";
  if (e.needsLyrics) return "Šis variklis kuria tik dainas su žodžiais.";
  return "";
};

export const SimpleFields = ({ f, set }) => (
  <Field label="Dainos aprašymas" right={
    <button data-testid="random-idea-button" onClick={() => set("description")(IDEAS[Math.floor(Math.random() * IDEAS.length)])}
      className="flex items-center gap-1 text-xs text-zinc-400 hover:text-[var(--c-orange)] transition-colors"><Dices className="w-3.5 h-3.5" />Atsitiktinė idėja</button>}>
    <textarea data-testid="song-description-input" rows={4} maxLength={1000} value={f.description}
      onChange={(e) => set("description")(e.target.value)} className={`${inputCls} resize-none text-base`}
      placeholder="Pvz.: Nostalgiška daina apie vasarą kaime pas senelius, su akordeonu..." />
  </Field>
);

export const CustomFields = ({ f, set, instr }) => (
  <>
    <Field label="Pavadinimas">
      <input data-testid="song-title-input" maxLength={80} value={f.title} onChange={(e) => set("title")(e.target.value)}
        className={inputCls} placeholder="Dainos pavadinimas" />
    </Field>
    {!instr && (
      <Field label="Dainos žodžiai">
        <LyricsEditor value={f.lyrics} onChange={set("lyrics")} idea={f.title || f.description}
          style={buildStyle(f.genre, f.moods, f.voice, f.extra)} length={f.length} llm={f.llm} />
      </Field>
    )}
  </>
);

const ComparePanel = ({ f, opts, pick, meta }) => {
  const cmp = findItem(opts, f.compare);
  return (
    <div data-testid="compare-panel" className="p-4 rounded-2xl border border-dashed border-[var(--c-violet)]/50 space-y-3 rise">
      <ModelSelect label="Palyginti su varikliu" items={opts} value={cmp.id} onChange={pick("compare")} testId="engine-compare-select" meta={meta} />
      <p data-testid="compare-total" className="text-xs text-zinc-400">Bus sukurtos 2 dainos su tais pačiais žodžiais ir stiliumi. Apytikslė kaina iš viso:{" "}
        <span className="font-mono text-emerald-400">{fmtUsd(engineCost(f.engine, f.length) + engineCost(cmp.id, f.length))}</span></p>
    </div>
  );
};

export const ModelPickers = ({ f, setF, instr }) => {
  const pick = (k) => (v) => { setPref(k, v); setF((s) => ({ ...s, [k]: v })); };
  const meta = engineMeta(f.length);
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <ModelSelect label="Muzikos variklis" items={ENGINES} value={f.engine} onChange={pick("engine")} testId="engine-select"
          hint={engineHint(findItem(ENGINES, f.engine))} meta={meta} />
        <ModelSelect label="Kalbos modelis (žodžiams)" items={LLMS} value={f.llm} onChange={pick("llm")} testId="llm-select" />
      </div>
      {f.compareOn && f.engine !== "yue_local" && <ComparePanel f={f} opts={compareOptions(f.engine, instr)} pick={pick} meta={meta} />}
      <p className="text-[11px] text-zinc-600">Kainos apytikslės ir skirtos tik muzikos generavimui (be viršelio ir žodžių).</p>
    </div>
  );
};

export const SongOptions = ({ f, set, eng, instr, comparing }) => (
  <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
    <Toggle testId="instrumental-switch" label="Instrumentinė" checked={instr} onChange={set("instrumental")} disabled={!!(eng.instrumentalOnly || eng.needsLyrics)} />
    <Toggle testId="public-switch" label="Rodyti „Atrasti“" checked={f.isPublic} onChange={set("isPublic")} />
    <Toggle testId="variants-switch" label="2 variantai" checked={!comparing && f.variants === 2} onChange={(v) => set("variants")(v ? 2 : 1)} disabled={comparing} />
    <Toggle testId="compare-switch" label="Palyginti variklius" checked={comparing} onChange={set("compareOn")} disabled={f.engine === "yue_local"} />
  </div>
);
